<div class="history-container">
  <div class="history-header">
    <h3>{{ "ALERT_HISTORY" | translate }}</h3>
    <div class="header-actions">
      <button mat-stroked-button class="refresh-button" data-testid="emaac-alert-history-refresh"
        [disabled]="loading" (click)="loadAlerts()">
        <span class="fa-light fa-fw fa-arrows-rotate" [class.fa-spin]="loading"></span>
        {{ "REFRESH" | translate }}
      </button>
      <button mat-icon-button class="close-button" mat-dialog-close>
        <mat-icon>close</mat-icon>
      </button>
    </div>
  </div>

  <div class="history-info">
    {{ "EMAAC_ALERT_HISTORY_INFO" | translate : { count: maxAlerts } }}
  </div>

  <div class="history-content">
    @if (loading && alerts.length === 0) {
      <div class="info-message">
        <span class="fa-light fa-fw fa-spinner fa-spin"></span> {{ "LOADING" | translate }}
      </div>
    } @else if (loadError) {
      <div class="info-message text-danger">
        <span class="fa-light fa-fw fa-exclamation-triangle"></span> {{ "ERROR" | translate }}
      </div>
    } @else if (alerts.length === 0) {
      <div class="info-message">
        <mat-icon>info</mat-icon> {{ "NO_RESULTS" | translate }}
      </div>
    } @else {
      <!-- Filters: a time range, and per column the values present in the data (as in the activity log) -->
      <div class="history-filters" role="toolbar">
        <div class="filter-group">
          <mat-form-field class="date-field" appearance="outline" floatLabel="always">
            <mat-label>{{ "REPORT_START_DATE" | translate }}</mat-label>
            <input matInput data-testid="emaac-history-from-date" [matDatepicker]="fromPicker" [max]="toDate"
              [(ngModel)]="fromDate" (dateChange)="applyFilters()" />
            <mat-datepicker-toggle matSuffix [for]="fromPicker"></mat-datepicker-toggle>
            <mat-datepicker #fromPicker></mat-datepicker>
          </mat-form-field>
          <mat-form-field class="time-field" appearance="outline" floatLabel="always">
            <mat-label>{{ "REPORT_START_TIME" | translate }}</mat-label>
            <input matInput appTimeInput type="text" pattern="^([01][0-9]|2[0-3]):[0-5][0-9]$" placeholder="00:00"
              [(ngModel)]="fromTime" (change)="applyFilters()" />
          </mat-form-field>
          <span class="range-separator">&ndash;</span>
          <mat-form-field class="date-field" appearance="outline" floatLabel="always">
            <mat-label>{{ "REPORT_END_DATE" | translate }}</mat-label>
            <input matInput data-testid="emaac-history-to-date" [matDatepicker]="toPicker" [min]="fromDate"
              [(ngModel)]="toDate" (dateChange)="applyFilters()" />
            <mat-datepicker-toggle matSuffix [for]="toPicker"></mat-datepicker-toggle>
            <mat-datepicker #toPicker></mat-datepicker>
          </mat-form-field>
          <mat-form-field class="time-field" appearance="outline" floatLabel="always">
            <mat-label>{{ "REPORT_END_TIME" | translate }}</mat-label>
            <input matInput appTimeInput type="text" pattern="^([01][0-9]|2[0-3]):[0-5][0-9]$" placeholder="23:59"
              [(ngModel)]="toTime" (change)="applyFilters()" />
          </mat-form-field>
        </div>

        <div class="filter-group">
          <app-multiselect-dropdown data-testid="emaac-history-customer-filter"
            [label]="'CUSTOMER' | translate"
            [placeholder]="'ALL_CLIENTS' | translate"
            [options]="options.customer"
            [enableSearch]="true"
            [(selected)]="filters.values.customer"
            (selectedChange)="applyFilters()">
          </app-multiselect-dropdown>
          <app-multiselect-dropdown data-testid="emaac-history-area-filter"
            [label]="'AREA' | translate"
            [placeholder]="'ALL_AREAS' | translate"
            [options]="options.area"
            [enableSearch]="true"
            [(selected)]="filters.values.area"
            (selectedChange)="applyFilters()">
          </app-multiselect-dropdown>
          <app-multiselect-dropdown data-testid="emaac-history-device-filter"
            [label]="'DEVICE_NAME' | translate"
            [placeholder]="'ALL_DEVICES' | translate"
            [options]="options.device"
            [enableSearch]="true"
            [(selected)]="filters.values.device"
            (selectedChange)="applyFilters()">
          </app-multiselect-dropdown>
          <app-multiselect-dropdown data-testid="emaac-history-handled-by-filter"
            [label]="'USER_OR_RECIPIENT' | translate"
            [placeholder]="'ALL_USERS' | translate"
            [options]="options.handledBy"
            [enableSearch]="true"
            [(selected)]="filters.values.handledBy"
            (selectedChange)="applyFilters()">
          </app-multiselect-dropdown>
          <button class="clear-filters-btn" data-testid="emaac-history-clear-filters"
            [disabled]="!filters.active" (click)="clearFilters()">
            {{ "CLEAR_FILTER" | translate | capitilize }}
          </button>
        </div>
      </div>

      @if (filteredAlerts.length === 0) {
        <div class="info-message">
          <mat-icon>info</mat-icon> {{ "NO_RESULTS" | translate }}
        </div>
      } @else {
      <table class="table table-hover tableLatest">
        <thead>
          <tr>
            <th></th>
            <th>{{ "START_TIME" | translate }}</th>
            <th>{{ "CUSTOMER" | translate }}</th>
            <th>{{ "AREA" | translate }}</th>
            <th>{{ "DEVICE_NAME" | translate }}</th>
            <th>{{ "USER_OR_RECIPIENT" | translate }}</th>
            <th>{{ "END_TIME" | translate }}</th>
          </tr>
        </thead>
        <tbody>
          @for (alert of filteredAlerts; track alert.alertId; let e = $even) {
            <!-- Alert header row -->
            <tr class="trCollapse" [ngClass]="{ trEven: e }" (click)="expandRow(alert)">
              <td><span class="fa-light" [ngClass]="alert.expanded ? 'fa-minus' : 'fa-plus'"></span></td>
              <td>{{ alert.receiveTime | moment : "l LTS" }}</td>
              <td>{{ alert.customerName }}</td>
              <td>{{ alert.locationName }}</td>
              <td>{{ alert.deviceName || alert.deviceId }}</td>
              <td [class.text-muted]="!alert.handledBy">{{ alert.handledByName }}</td>
              <td>{{ alert.completeTime | moment : "l LTS" }}</td>
            </tr>
            <!-- Alert details -->
            @if (alert.expanded) {
              <tr [ngClass]="{ trEven: e }">
                <td colspan="7" class="tdDetails">
                  <div class="row">
                    <!-- Device -->
                    <div class="col-md-3">
                      <div class="section-header"><span class="fa-light fa-fw fa-phone-laptop"></span> {{ "DEVICE" | translate }}</div>
                      <div><strong>{{ "SERIAL_NUMBER" | translate }}:</strong> {{ alert.deviceId }}</div>
                      @if (alert.baseStationId || alert.baseStationName) {
                        <div>
                          <strong>{{ "BASESTATION" | translate }}:</strong>
                          {{ alert.baseStationName }}
                          @if (alert.baseStationId) {
                            ({{ alert.baseStationId }})
                          }
                        </div>
                      }
                      @if (getAddress(alert); as address) {
                        <div>
                          <strong>{{ "ADDRESS" | translate }}:</strong> {{ address }}
                          @if (alert.addressFromBaseStation) {
                            <span class="text-muted">({{ "BASESTATION" | translate }})</span>
                          }
                        </div>
                      }
                      @if (alert.baseStationPhoneNumber) {
                        <div>
                          <!-- tel: link opens the operator's calling software, as in the EMAAC phonebook -->
                          <a [href]="'tel:' + alert.baseStationPhoneNumber" class="cursorPointerHover" data-testid="emaac-history-call-base">
                            <span class="fa-light fa-fw fa-phone"></span> {{ alert.baseStationPhoneNumber }}
                          </a>
                        </div>
                      }
                      @if (alert.information) {
                        <div class="mt-2">
                          <strong>{{ "DEVICE_ADDITIONAL_INFO_SHORT" | translate }}:</strong>
                          <!-- Phone numbers in the text become tel: links -->
                          <app-phone-text class="free-text" [text]="alert.information"></app-phone-text>
                        </div>
                      }
                      @if (alert.moreInformation) {
                        <div class="mt-2">
                          <strong>{{ "DEVICE_ADDITIONAL_ALARM_INFO" | translate }}:</strong>
                          <app-phone-text class="free-text" [text]="alert.moreInformation"></app-phone-text>
                        </div>
                      }
                    </div>

                    <!-- Everon Mobile, only when the alert was forwarded and an Everon Mobile user handled it.
                         A forward with no one online in Everon Mobile comes back to EMAAC, so there is nothing
                         to show. Left out otherwise so the following columns move left instead of leaving a gap. -->
                    @if (alert.forwarded && alert.forwardedTo) {
                      <div class="col-md-3">
                        <div class="section-header"><span class="fa-light fa-fw fa-mobile"></span> Everon Mobile</div>
                        <div><strong>{{ "LOG_NEW_ALERT" | translate }}:</strong> {{ alert.emaReceivedTime | moment : "l LTS" }}</div>
                        <div><strong>{{ "STARTED_BY" | translate }}:</strong> {{ alert.forwardedTo.userName || "-" }}</div>
                        <div><strong>{{ "LOG_NEW_STATE_STARTED" | translate }}:</strong> {{ alert.forwardedTo.time | moment : "l LTS" }}</div>
                      </div>
                    }

                    <!-- Alarm -->
                    <div class="col-md-3">
                      <div class="section-header"><span class="fa-light fa-fw fa-bell"></span> {{ "EVENT" | translate }}</div>
                      <div>
                        <strong>{{ "ALARM_TYPE" | translate }}:</strong>
                        {{ alert.activationGroup | getActivationName : alert.activationNode : alert.deviceType : alert.customActivationName : null }}
                      </div>
                      @if (alert.alarmReasons?.length > 0) {
                        <div class="mt-2"><strong>{{ "ALARM_REASONS" | translate }}:</strong></div>
                        <ul class="reason-list">
                          @for (item of alert.alarmReasons | removeAlarmReaonDuplicates; track item) {
                            <li>
                              {{ (item.alarmReasonCategory | alarmCategory : alarmReasonTranslations) + " - " + (item.alarmReason | alarmReason : alarmReasonTranslations) }}
                            </li>
                          }
                        </ul>
                      }
                    </div>

                    <!-- Notes -->
                    <div class="col-md-3">
                      <div class="section-header"><span class="fa-light fa-fw fa-note-sticky"></span> {{ "NOTES" | translate }}</div>
                      @for (note of alert.notes; track $index) {
                        <div class="mb-2">
                          <div>{{ note.userName }}</div>
                          <div>{{ note.time | moment : "l LTS" }}</div>
                          <app-text-format [text]="note.text"></app-text-format>
                        </div>
                      }
                    </div>
                  </div>
                  <!-- Customer phonebook, the same as in alert handling. IDs carry the server prefix it needs. -->
                  @if (alert.customerId && alert.alertId) {
                    <div class="d-flex mt-2 justify-content-end">
                      <app-phonebook-button
                        [customerId]="alert.customerId"
                        [serverId]="alert.alertId"
                      ></app-phonebook-button>
                    </div>
                  }
                </td>
              </tr>
            }
          }
        </tbody>
      </table>
      }
    }
  </div>
</div>
